<template>
  <div class="scout-activity-analytics">
    <!-- Header with navigation back -->
    <div class="section-header">
      <button @click="$emit('navigate-back')" class="back-button">
        <i class="bi bi-arrow-left"></i>
        Back to Analytics Dashboard
      </button>
      <h3>Scout Activity Analysis</h3>
      <p>Track scout performance, efficiency, and coverage metrics</p>
    </div>

    <!-- KPI Cards -->
    <div class="kpi-cards">
      <div class="kpi-card">
        <div class="kpi-icon">
          <i class="bi bi-calendar-check"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ totalGamesAttended }}</h4>
          <p>Games Attended</p>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-icon">
          <i class="bi bi-people"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ totalPlayersAssessed }}</h4>
          <p>Players Assessed</p>
        </div>
      </div>

      <div class="kpi-card">
        <div class="kpi-icon">
          <i class="bi bi-trophy"></i>
        </div>
        <div class="kpi-content">
          <h4>{{ overallConversionRate }}%</h4>
          <p>Lead Conversion</p>
        </div>
      </div>
    </div>

    <!-- Analytics Grid -->
    <div class="analytics-grid">
      <!-- Games Attended Chart -->
      <div class="chart-container">
        <div class="chart-header">
          <h4>Games Attended by Scout</h4>
          <p class="chart-subtitle">Monthly scout activity overview</p>
        </div>
        <div class="chart-placeholder">
          <EnhancedBarChart
            :chart-data="gamesAttendedChartData"
            :chart-options="chartOptions"
          />
        </div>
      </div>

      <!-- Regional Coverage -->
      <div class="chart-container">
        <div class="chart-header">
          <h4>Regional Scouting Coverage</h4>
          <p class="chart-subtitle">Geographic distribution of activity</p>
        </div>
        <div class="coverage-heatmap">
          <div
            v-for="region in regionalCoverage"
            :key="region.name"
            class="region-card"
            :style="getRegionStyle(region.intensity)"
            @click="onRegionClick(region)"
          >
            <h5>{{ region.name }}</h5>
            <div class="region-stats">
              <span class="games-count">{{ region.gamesCount }} games</span>
              <span class="players-count"
                >{{ region.playersCount }} players</span
              >
            </div>
            <div class="region-intensity">
              <div
                class="intensity-bar"
                :style="{ width: region.intensity + '%' }"
              ></div>
            </div>
          </div>
        </div>
      </div>

      <!-- Scout Performance Table -->
      <div class="chart-container full-width">
        <div class="chart-header">
          <h4>Scout Performance Summary</h4>
          <p class="chart-subtitle">Key performance metrics by scout</p>
        </div>
        <div class="performance-table">
          <table class="scouts-table">
            <thead>
              <tr>
                <th>Scout</th>
                <th>Games</th>
                <th>Players</th>
                <th>Leads</th>
                <th>Conversion %</th>
                <th>Efficiency</th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="scout in scoutPerformanceData"
                :key="scout.id"
                class="performance-row"
                @click="onScoutClick(scout)"
              >
                <td class="scout-name">{{ scout.name }}</td>
                <td>{{ scout.gamesAttended }}</td>
                <td>{{ scout.playersAssessed }}</td>
                <td>{{ scout.leadsGenerated }}</td>
                <td>
                  <span
                    class="conversion-rate"
                    :class="getConversionClass(scout.conversionRate)"
                  >
                    {{ scout.conversionRate }}%
                  </span>
                </td>
                <td>
                  <div class="efficiency-score">
                    <span>{{ scout.efficiencyScore }}</span>
                    <div class="efficiency-bar">
                      <div
                        class="efficiency-fill"
                        :style="{
                          width: (scout.efficiencyScore / 10) * 100 + '%',
                        }"
                      ></div>
                    </div>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import EnhancedBarChart from "../charts/EnhancedBarChart.vue";

export default {
  name: "ScoutActivityAnalytics",
  components: {
    EnhancedBarChart,
  },
  props: {
    analyticsData: {
      type: Object,
      default: () => ({}),
    },
    dateRange: {
      type: Array,
      default: () => [],
    },
    filters: {
      type: Object,
      default: () => ({}),
    },
    gameAttendanceData: {
      type: Array,
      default: () => [],
    },
    allScoutsData: {
      type: Array,
      default: () => [],
    },
  },
  emits: ["data-point-click", "navigate-back"],
  computed: {
    totalGamesAttended() {
      return this.gameAttendanceData ? this.gameAttendanceData.length : 0;
    },

    totalPlayersAssessed() {
      if (!this.gameAttendanceData) return 0;

      return this.gameAttendanceData.reduce((total, game) => {
        if (game.playersGameData && Array.isArray(game.playersGameData)) {
          return total + game.playersGameData.length;
        }
        return total;
      }, 0);
    },

    overallConversionRate() {
      if (!this.gameAttendanceData || this.gameAttendanceData.length === 0)
        return 0;

      let totalPlayers = 0;
      let totalLeads = 0;

      this.gameAttendanceData.forEach((game) => {
        if (game.playersGameData && Array.isArray(game.playersGameData)) {
          totalPlayers += game.playersGameData.length;

          // Count leads as players with average rating >= 6
          const leads = game.playersGameData.filter((player) => {
            const avgRating =
              ((parseInt(player.attitude) || 0) +
                (parseInt(player.skill) || 0) +
                (parseInt(player.physique) || 0) +
                (parseInt(player.mentality) || 0) +
                (parseInt(player.leadership) || 0) +
                (parseInt(player.professionalism) || 0)) /
              6;
            return avgRating >= 6;
          }).length;

          totalLeads += leads;
        }
      });

      return totalPlayers > 0
        ? Math.round((totalLeads / totalPlayers) * 100)
        : 0;
    },

    scoutPerformanceData() {
      if (!this.gameAttendanceData || this.gameAttendanceData.length === 0) {
        return [];
      }

      const scoutMetrics = {};

      this.gameAttendanceData.forEach((game) => {
        const scoutId = game.scoutId || game.scout_id;
        if (scoutId) {
          if (!scoutMetrics[scoutId]) {
            scoutMetrics[scoutId] = {
              id: scoutId,
              name: this.getScoutName(scoutId),
              gamesAttended: 0,
              playersAssessed: 0,
              leadsGenerated: 0,
              conversionRate: 0,
              efficiencyScore: 0,
            };
          }

          scoutMetrics[scoutId].gamesAttended++;

          if (game.playersGameData && Array.isArray(game.playersGameData)) {
            scoutMetrics[scoutId].playersAssessed +=
              game.playersGameData.length;

            // Count leads as players with average rating >= 6
            const leads = game.playersGameData.filter((player) => {
              const avgRating =
                ((parseInt(player.attitude) || 0) +
                  (parseInt(player.skill) || 0) +
                  (parseInt(player.physique) || 0) +
                  (parseInt(player.mentality) || 0) +
                  (parseInt(player.leadership) || 0) +
                  (parseInt(player.professionalism) || 0)) /
                6;
              return avgRating >= 6;
            }).length;

            scoutMetrics[scoutId].leadsGenerated += leads;
          }
        }
      });

      // Calculate conversion rates and efficiency scores
      return Object.values(scoutMetrics)
        .map((scout) => {
          scout.conversionRate =
            scout.playersAssessed > 0
              ? Math.round((scout.leadsGenerated / scout.playersAssessed) * 100)
              : 0;

          // Efficiency score: leads-per-game on a 0–10 scale
          // (1 lead per game = 10, scaled linearly).
          const leadsPerGame =
            scout.gamesAttended > 0
              ? scout.leadsGenerated / scout.gamesAttended
              : 0;
          scout.efficiencyScore = Math.min(10, Math.round(leadsPerGame * 10));

          return scout;
        })
        .sort((a, b) => b.gamesAttended - a.gamesAttended);
    },

    gamesAttendedChartData() {
      const scoutData = this.scoutPerformanceData.slice(0, 5); // Top 5 scouts

      return {
        labels: scoutData.map((scout) => scout.name),
        datasets: [
          {
            label: "Games Attended",
            data: scoutData.map((scout) => scout.gamesAttended),
            backgroundColor: "rgba(54, 162, 235, 0.8)",
            borderColor: "rgba(54, 162, 235, 1)",
            borderWidth: 2,
          },
        ],
      };
    },

    chartOptions() {
      return {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            title: {
              display: true,
              text: "Games Attended",
            },
          },
          x: {
            title: {
              display: true,
              text: "Scout",
            },
          },
        },
      };
    },

    regionalCoverage() {
      if (!this.gameAttendanceData || this.gameAttendanceData.length === 0) {
        return [];
      }

      const regionMap = {};

      this.gameAttendanceData.forEach((game) => {
        let region = "Unknown";

        // Extract region from venue or location
        if (game.venue) {
          region =
            game.venue.split(",")[0] || game.venue.split(" ")[0] || region;
        } else if (game.location) {
          region =
            game.location.split(",")[0] ||
            game.location.split(" ")[0] ||
            region;
        }

        if (!regionMap[region]) {
          regionMap[region] = {
            name: region,
            gamesCount: 0,
            playersCount: 0,
            intensity: 0,
          };
        }

        regionMap[region].gamesCount++;
        if (game.playersGameData && Array.isArray(game.playersGameData)) {
          regionMap[region].playersCount += game.playersGameData.length;
        }
      });

      // Calculate intensity relative to highest activity region
      const regions = Object.values(regionMap);
      const maxGames = Math.max(...regions.map((r) => r.gamesCount));

      return regions
        .map((region) => ({
          ...region,
          intensity:
            maxGames > 0 ? Math.round((region.gamesCount / maxGames) * 100) : 0,
        }))
        .sort((a, b) => b.gamesCount - a.gamesCount)
        .slice(0, 6); // Top 6 regions
    },
  },

  methods: {
    getConversionClass(rate) {
      if (rate >= 25) return "conversion-excellent";
      if (rate >= 15) return "conversion-good";
      if (rate >= 10) return "conversion-average";
      return "conversion-poor";
    },

    getRegionStyle(intensity) {
      const alpha = intensity / 100;
      return {
        backgroundColor: `rgba(54, 162, 235, ${alpha * 0.3})`,
        borderColor: `rgba(54, 162, 235, ${alpha})`,
      };
    },

    getScoutName(scoutId) {
      if (this.allScoutsData && Array.isArray(this.allScoutsData)) {
        const scout = this.allScoutsData.find((s) => s.id == scoutId);
        if (scout) {
          return (
            scout.name ||
            (scout.firstName && scout.lastName
              ? `${scout.firstName} ${scout.lastName}`
              : "Unknown Scout")
          );
        }
      }
      return `Scout ${scoutId}`;
    },

    onRegionClick(region) {
      this.$emit("data-point-click", {
        title: `${region.name} - Regional Analysis`,
        component: "RegionalDetailModal",
        data: region,
        context: { type: "regional-coverage" },
      });
    },

    onScoutClick(scout) {
      this.$emit("data-point-click", {
        title: `${scout.name} - Performance Analysis`,
        component: "ScoutPerformanceModal",
        data: scout,
        context: { type: "scout-performance" },
      });
    },
  },
};
</script>

<style lang="scss" scoped>
.scout-activity-analytics {
  .section-header {
    margin-bottom: 2rem;

    .back-button {
      display: flex;
      align-items: center;
      gap: 0.5rem;
      background: none;
      border: 1px solid #dee2e6;
      border-radius: 6px;
      padding: 0.5rem 1rem;
      color: #6c757d;
      cursor: pointer;
      margin-bottom: 1rem;
      transition: all 0.2s ease;

      &:hover {
        background-color: #f8f9fa;
        color: #495057;
      }
    }

    h3 {
      color: #2c3e50;
      font-size: 1.5rem;
      margin-bottom: 0.5rem;
    }

    p {
      color: #6c757d;
      margin: 0;
    }
  }

  .kpi-cards {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
    gap: 1.5rem;
    margin-bottom: 2rem;

    .kpi-card {
      background: white;
      border-radius: 8px;
      padding: 1.5rem;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      display: flex;
      align-items: center;
      gap: 1rem;
      transition:
        transform 0.2s ease,
        box-shadow 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
      }

      .kpi-icon {
        width: 60px;
        height: 60px;
        border-radius: 50%;
        background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
        display: flex;
        align-items: center;
        justify-content: center;
        color: white;
        font-size: 1.5rem;
      }

      .kpi-content {
        flex: 1;

        h4 {
          font-size: 1.8rem;
          font-weight: 700;
          margin: 0 0 0.25rem 0;
          color: #2c3e50;
        }

        p {
          margin: 0 0 0.5rem 0;
          color: #6c757d;
          font-size: 0.9rem;
        }

        .kpi-trend {
          font-size: 0.8rem;
          font-weight: 500;

          &.trend-positive {
            color: #28a745;
          }

          &.trend-negative {
            color: #dc3545;
          }

          &.trend-neutral {
            color: #6c757d;
          }
        }
      }
    }
  }

  .analytics-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    margin-bottom: 2rem;

    /* On smaller screens, stack vertically */
    @media (max-width: 1200px) {
      grid-template-columns: 1fr;
    }

    .chart-container {
      background: white;
      border-radius: 8px;
      padding: 1.5rem;
      box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
      min-height: 400px;
      display: flex;
      flex-direction: column;

      &.full-width {
        grid-column: 1 / -1;
        min-height: auto;
      }

      .chart-header {
        margin-bottom: 1rem;

        h4 {
          color: #2c3e50;
          font-size: 1.1rem;
          margin: 0 0 0.25rem 0;
        }

        .chart-subtitle {
          color: #6c757d;
          font-size: 0.85rem;
          margin: 0;
        }
      }

      .chart-placeholder {
        height: 300px;
        flex: 1;
        min-height: 250px;
      }
    }
  }

  .coverage-heatmap {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    flex: 1;

    .region-card {
      padding: 1rem;
      border-radius: 6px;
      border: 2px solid;
      cursor: pointer;
      transition: all 0.2s ease;

      &:hover {
        transform: translateY(-2px);
        box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
      }

      h5 {
        margin: 0 0 0.5rem 0;
        font-size: 0.9rem;
        font-weight: 600;
      }

      .region-stats {
        display: flex;
        flex-direction: column;
        gap: 0.25rem;
        margin-bottom: 0.5rem;

        span {
          font-size: 0.75rem;
          color: #6c757d;
        }
      }

      .region-intensity {
        .intensity-bar {
          height: 4px;
          background-color: #e9ecef;
          border-radius: 2px;
          overflow: hidden;
        }
      }
    }
  }

  .performance-table {
    overflow-x: auto;

    .scouts-table {
      width: 100%;
      border-collapse: collapse;

      th,
      td {
        padding: 0.75rem;
        text-align: left;
        border-bottom: 1px solid #dee2e6;
      }

      th {
        background-color: #f8f9fa;
        font-weight: 600;
        color: #495057;
      }

      .performance-row {
        cursor: pointer;
        transition: background-color 0.2s ease;

        &:hover {
          background-color: #f8f9fa;
        }
      }

      .scout-name {
        font-weight: 500;
        color: #2c3e50;
      }

      .conversion-rate {
        padding: 0.25rem 0.5rem;
        border-radius: 4px;
        font-size: 0.85rem;
        font-weight: 500;

        &.conversion-excellent {
          background-color: #d4edda;
          color: #155724;
        }

        &.conversion-good {
          background-color: #d1ecf1;
          color: #0c5460;
        }

        &.conversion-average {
          background-color: #fff3cd;
          color: #856404;
        }

        &.conversion-poor {
          background-color: #f8d7da;
          color: #721c24;
        }
      }

      .efficiency-score {
        display: flex;
        align-items: center;
        gap: 0.5rem;

        .efficiency-bar {
          flex: 1;
          height: 6px;
          background-color: #e9ecef;
          border-radius: 3px;
          overflow: hidden;

          .efficiency-fill {
            height: 100%;
            background: linear-gradient(
              90deg,
              #dc3545 0%,
              #ffc107 50%,
              #28a745 100%
            );
            transition: width 0.3s ease;
          }
        }
      }
    }
  }
}

/* Mobile Responsiveness */
@media (max-width: 768px) {
  .scout-activity-analytics {
    .analytics-grid {
      grid-template-columns: 1fr;
      gap: 1.5rem;
    }

    .chart-container {
      min-height: 350px;
      padding: 1rem;

      .chart-placeholder {
        height: 250px;
        min-height: 200px;
      }
    }

    .coverage-heatmap {
      grid-template-columns: 1fr 1fr;
      gap: 0.75rem;

      .region-card {
        padding: 0.75rem;
      }
    }

    .kpi-cards {
      grid-template-columns: 1fr 1fr;
      gap: 1rem;

      .kpi-card {
        padding: 1rem;
        flex-direction: column;
        text-align: center;

        .kpi-icon {
          width: 50px;
          height: 50px;
          font-size: 1.25rem;
        }

        .kpi-content h4 {
          font-size: 1.5rem;
        }
      }
    }

    .performance-table {
      font-size: 0.85rem;

      .scouts-table {
        th,
        td {
          padding: 0.5rem 0.25rem;
        }
      }
    }
  }
}

@media (max-width: 480px) {
  .scout-activity-analytics {
    .kpi-cards {
      grid-template-columns: 1fr;
    }

    .coverage-heatmap {
      grid-template-columns: 1fr;
    }

    .chart-container {
      min-height: 300px;

      .chart-placeholder {
        height: 200px;
        min-height: 180px;
      }
    }
  }
}
</style>
